/* eslint-disable no-lonely-if */
/* eslint-disable no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ViewerLayout } from './styled/ViewerLayout';
import { ViewWrapper } from './styled/ViewWrapper';
import { View } from './View';
import { changeFullViews, isFullView, selectView, addToViewsGroup } from './Layout/LayoutUtils';
import useKeyPress from '../../../util/useKeypress';
import { drop, allowDrop } from '../../../util/DragnDrop';
import { getStudy } from '../../../util/elementsUtils';
import { findStudySeries } from '../../../util/studiesManager';
import { cleanPrefix, WADO_RS_WEB_SERVICE_TYPE, WADO_URI_WEB_SERVICE_TYPE } from '../../../util/useServerConfig';
import isDocument from '../../../model/isDocument';
import isWaveform from '../../../model/isWaveform';
import isImage from '../../../model/isImage';
import { isMultiFrame } from '../../../model/SupportsInstances';
import { ViewRightWrapper } from './styled/ViewRightWrapper';
import store from '../../../store/store';
import isNotReadable from '../../../model/isNotReadable';
import { setLayoutGrid, setLayoutElements } from '../../../actions/LayoutActions';
import { applyBestHangingProtocol } from '../../../protocols/hangingProtocolManager';
import { resizeCornerstoneViewports } from '../../../util/resizeCornerstoneViewports';
import { getPacsEndpoints } from '../../../util/getConfig';
import { getVolumeManager } from './getVolumeManager';
import {
  getCrossWindowActiveViewport,
  isCrossWindowViewportActive,
  createActiveViewportContext,
  setCrossWindowActiveViewport,
} from './crossWindow2dReferenceLines';

export const buildLayoutGrid = ({ layout, patient, ctrlPressed, commandPressed, is3DModeEnabled, isMPRModeEnabled }) => {
  const views = [];
  let selectedViewIndex = isFullView(layout) ? layout.selectedViewIndex : 0;

  for (let i = 0; i < layout.columns; i++) {
    for (let j = 0; j < layout.rows; j++) {
      let view;
      const layoutElement = layout.elements[selectedViewIndex];
      if (layoutElement) {
        const { StudyInstanceUID, SeriesInstanceUID } = layoutElement;
        const study = getStudy(patient, StudyInstanceUID);
        const series = findStudySeries({ study, SeriesInstanceUID });

        if (is3DModeEnabled) {
          view = get3DModeViewerView(layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed);
        } else if (isMPRModeEnabled) {
          view = getMPRModeViewerView(layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed);
        } else {
          view = getViewerView(layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed);
        }
      } else {
        view = getViewerView(layout, selectedViewIndex, {}, {}, ctrlPressed, commandPressed);
      }
      views.push(view);
      selectedViewIndex += 1;
    }
  }

  return views;
};

const get3DModeViewerView = (layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed) => {
  const isRightView = selectedViewIndex === 8;
  if ([1, 2, 4, 5, 7].includes(selectedViewIndex)) {
    series = {};
  }
  const view = getViewerView(layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed, isRightView);
  return view;
};

const getMPRModeViewerView = (layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed) => {
  const isRightView = selectedViewIndex === 5;
  if ([1, 2, 4].includes(selectedViewIndex)) {
    series = {};
  }
  const view = getViewerView(layout, selectedViewIndex, series, layoutElement, ctrlPressed, commandPressed, isRightView);
  return view;
};

export const getViewerView = (layout, key, series, layoutElement, ctrlPressed, commandPressed, isRightView) => {
  const ViewerViewWrapper = isRightView ? ViewRightWrapper : ViewWrapper;
  const crossWindowSelection = isCrossWindowViewportActive(key);

  return (
    <ViewerViewWrapper
      selectedView={crossWindowSelection === null ? layout.viewsGroup.includes(key) : crossWindowSelection}
      onClick={() => select(key, layout, ctrlPressed, commandPressed)}
      onDoubleClick={() => changeFullViews(layout)}
      key={key}
      onDrop={(e) => drop(e, key)}
      onDragOver={(e) => allowDrop(e, key)}
      columns={layout.columns}
      rows={layout.rows}
      series={series}
      layoutElement={layoutElement}
      is3DModeEnabled={layout.is3DModeEnabled}
      isMPRModeEnabled={layout.isMPRModeEnabled}
    >
      {Object.keys(layoutElement).length > 0 ? getView(series, layoutElement, key) : <></>}
    </ViewerViewWrapper>
  );
};

export const getView = (series, layoutElement, index) => {
  if (!series) return;

  if (Object.keys(series).length === 0) return;

  const config = store.getState().config;

  const { wadoUriEnabled } = getPacsEndpoints(config);

  const webServiceType = wadoUriEnabled ? WADO_URI_WEB_SERVICE_TYPE : config.WEB_SERVICE_TYPE || WADO_RS_WEB_SERVICE_TYPE;

  const { SeriesDate, SeriesNumber, SeriesTime, SeriesDescription, Modality: SeriesModality } = series;

  const parsedInstances = [];

  const instances = series.instances || [];

  const readableInstances = instances.filter((instance) => !isNotReadable(instance.metadata.SOPClassUID));

  const imageNumberSet = new Set();
  readableInstances.forEach((instance) => {
    imageNumberSet.add(instance.metadata.InstanceNumber);
  });

  const getDirectionVector = (v1, v2) => {
    if (!v2) return v1;
    const v = v2.map((val, i) => val - v1[i]);
    const length = Math.sqrt(v[0] ** 2 + v[1] ** 2 + v[2] ** 2);
    return v.map((val) => val / length);
  };

  let orderByInstanceNumber = true;
  const notOrdered = [];
  if (imageNumberSet.size !== readableInstances.length && readableInstances[0].metadata.ImagePositionPatient) {
    // debugger;
    orderByInstanceNumber = false;
    const originIPP = readableInstances[0].metadata.ImagePositionPatient;
    const secondIPP = readableInstances[1].metadata.ImagePositionPatient;
    // debugger;

    const dVector = getDirectionVector(originIPP, secondIPP);

    const threshold = 0.3;
    for (let i = 0; i < readableInstances.length; i++) {
      const instance = readableInstances[i];
      const ipp = instance.metadata.ImagePositionPatient;

      if (!ipp) continue;

      if (ipp !== originIPP) {
        const imageDVector = getDirectionVector(originIPP, ipp);
        // TODO: comprobar si este calculo puede fallar, puede ser necesario comprobar cada coordenada individualmente contra un número más pequeño
        const diff = Math.abs(dVector[0] - imageDVector[0] + dVector[1] - imageDVector[1] + dVector[2] - imageDVector[2]);
        if (diff >= threshold) {
          orderByInstanceNumber = true;
          break;
        }
      }

      const v1 = ipp.map((v, index) => v - originIPP[index]);

      const dotProduct = v1[0] * dVector[0] + v1[1] * dVector[1] + v1[2] * dVector[2];

      notOrdered[i] = [dotProduct, instance];
    }
  }

  const ordered = orderByInstanceNumber ? readableInstances.sort((a, b) => a.metadata.InstanceNumber - b.metadata.InstanceNumber) : notOrdered.sort((a, b) => a[0] - b[0]).map((v) => v[1]);

  ordered.forEach((instance) => {
    // multiframe
    if (isMultiFrame(instance)) {
      for (let i = 1; i < instance.metadata.NumberOfFrames; i++) {
        const url = `mywadors:${instance.baseWadoRsUri}/frames/${i}`;
        parsedInstances.push(url);
      }
    } else {
      // singleframe
      if (webServiceType === WADO_URI_WEB_SERVICE_TYPE) {
        if ((isDocument(instance.metadata.SOPClassUID) || isWaveform(instance.metadata.SOPClassUID)) && !isImage(instance.metadata.SOPClassUID)) {
          parsedInstances.push(`${cleanPrefix(instance.wadouri)}`);
        } else {
          parsedInstances.push(`mywadouri:${cleanPrefix(instance.wadouri)}`);
        }
      } else {
        if (isDocument(instance.metadata.SOPClassUID) || isWaveform(instance.metadata.SOPClassUID)) {
          parsedInstances.push(`${cleanPrefix(instance.baseWadoRsUri)}`);
        } else {
          parsedInstances.push(`mywadors:${instance.baseWadoRsUri}`);
        }
        // some SecondaryCaptureImage can be document, for i.e. Olive Guma (see isDocument.js l.21 -> secondaryImageCapture)
      }
    }
  });

  return <View instanceIds={parsedInstances} index={index} layoutElement={layoutElement} seriesDetails={{ SeriesDate, SeriesNumber, SeriesTime, SeriesDescription, SeriesModality }} />;
};

// filtro, si ya esta seleccion no hacer dispatch
// esta funcion podria pertenecer al layout utils?
const select = (key, layout, ctrlPressed, commandPressed) => {
  if (ctrlPressed || commandPressed || layout?.viewsGroup?.includes(key)) {
    setCrossWindowActiveViewport(key, createActiveViewportContext(layout, key));
  }
  if (layout !== undefined && layout.viewsGroup !== undefined && layout.viewsGroup.length >= 0 && !layout.viewsGroup.includes(key)) {
    commandPressed || ctrlPressed ? addToViewsGroup(key) : selectView(key);
  } else {
    (commandPressed || ctrlPressed) && addToViewsGroup(key);
  }
};

// escucha los cambios de layout y pinta las series
export const ViewManager = () => {
  const patient = useSelector((state) => state.patient);
  const layout = useSelector((state) => state.layout);
  const dispatch = useDispatch();
  const [hpAppliedForStudy, setHpAppliedForStudy] = useState(null);
  const [, setActiveViewport] = useState(getCrossWindowActiveViewport());
  const viewerLayoutRef = useRef(null);
  const is3DModeEnabled = layout.is3DModeEnabled;
  const isMPRModeEnabled = layout.isMPRModeEnabled;
  const ctrlPressed = useKeyPress('Control');
  const commandPressed = useKeyPress('Meta');

  useEffect(() => {
    const handleActiveViewportChange = () => setActiveViewport(getCrossWindowActiveViewport());
    window.addEventListener('viewer-active-viewport-changed', handleActiveViewportChange);
    return () => window.removeEventListener('viewer-active-viewport-changed', handleActiveViewportChange);
  }, []);

  useEffect(() => {
    if (!patient || !patient.studies || patient.studies.length === 0) return;
    const mainStudyId = patient.mainStudyId || patient.studies[0].StudyInstanceUID;
    if (hpAppliedForStudy === mainStudyId) return;
    const result = applyBestHangingProtocol(patient);
    if (result) {
      dispatch(setLayoutGrid(result.layout.columns, result.layout.rows));
      dispatch(setLayoutElements(result.elements));
    }
    setHpAppliedForStudy(mainStudyId);
  }, [patient, dispatch, hpAppliedForStudy]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(resizeCornerstoneViewports);

    return () => window.cancelAnimationFrame(frame);
  }, [layout.columns, layout.rows]);

  useEffect(() => {
    const viewerLayout = viewerLayoutRef.current;
    if (!viewerLayout) return undefined;

    let resizeTimeout;
    const resizeViewports = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCornerstoneViewports();
        if (is3DModeEnabled || isMPRModeEnabled) {
          getVolumeManager()?.resizeCanvasAndDivs();
        }
      }, 100);
    };

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', resizeViewports);
      return () => {
        clearTimeout(resizeTimeout);
        window.removeEventListener('resize', resizeViewports);
      };
    }

    const resizeObserver = new ResizeObserver(resizeViewports);
    resizeObserver.observe(viewerLayout);
    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [is3DModeEnabled, isMPRModeEnabled]);

  const views = buildLayoutGrid({ layout, patient, ctrlPressed, commandPressed, is3DModeEnabled, isMPRModeEnabled });
  return <ViewerLayout className="ViewerLayout" ref={viewerLayoutRef}>{views}</ViewerLayout>;
};

export default ViewManager;
